import React, {
  useState,
  /*, useMemo*/
  useEffect,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import { Disclosure } from "@headlessui/react";
import { useNotification } from "@hooks/useNotification";
import {
  ShapeCompletDropdown,
  SvgBlackTrash,
  SvgChangeName,
  SvgDuplicate
} from "@routes/IconeSvg";
import { isEqual } from "lodash";
import { Menu } from "@components/tailwindComponents";
import { OnboardingProtocol } from "@services/all.services";
import ModalDelete from "../components/ModalDelete";
import ProtocolTaskList from "./protocolTaskList";

function ItemsIntegrationProtocol(props) {
  const { showError, showSuccess } = useNotification();
  const [showModalDelete, setShowModal] = useState(false);
  const { t } = useTranslation("beginEnd.protocol");
  /* -- Protocol input management -- */
  const [inputWritable, setInputWritable] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const inputRef = useRef();
  useEffect(() => {
    if ((props.isCreating || inputWritable) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [props.isCreating, inputWritable]);

  async function handleUpsertProtocol() {
    if (!inputValue) {
      props.isCreating && props.closeCreation();
      setInputWritable(false);
      return;
    }

    try {
      if (props.isCreating) {
        props.closeCreation();
        const requestData = {
          name: inputValue,
          Enterprise: localStorage.getItem("enterpriseId")
        };
        const protocolCreated = await OnboardingProtocol.createProtocol(
          requestData
        );
        if (isEqual(protocolCreated, "FORBIDDEN")) {
          props.isCreating && props.closeCreation();
          setInputWritable(false);
          showError("already_exists", { object: "protocolUpper" });
          return;
        }
        showSuccess("creation_action_success", { object: "protocol" });
        return;
      }

      setInputWritable(false);
      const requestData = {
        _id: props.protocol._id,
        name: inputValue,
        Enterprise: localStorage.getItem("enterpriseId")
      };
      const protocolUpdated = await OnboardingProtocol.updateProtocol(
        requestData
      );
      if (isEqual(protocolUpdated, "FORBIDDEN")) {
        props.isCreating && props.closeCreation();
        setInputWritable(false);
        showError("already_exists", { object: "protocolUpper" });
        return;
      }
      showSuccess("update_action_success", { object: "protocol" });
    } catch (err) {
      console.error(err);
      showError("error");
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      handleUpsertProtocol();
    } else if (event.keyCode === 32) {
      event.stopPropagation();
      setInputValue(inputRef.current.value.concat(" "));
    }
  }

  /* -- Togglable elements -- */
  // const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  // function toggleCollapse() {
  //   setIsCollapseOpen(prevstate => !prevstate);
  // }

  // const isCollapseActive = useMemo(() => {
  //   return isCollapseOpen && !props.isCreating && !inputWritable;
  // }, [isCollapseOpen, props.isCreating, inputWritable]);

  /* -- Event handling -- */
  function onUpdateClicked() {
    setInputValue(props.protocol.name);
    setInputWritable(true);
  }

  async function deleteProtocol() {
    try {
      await OnboardingProtocol.deleteProtocol(props.protocol._id);
      showSuccess("delete_action_success", { object: "protocol" });
      setShowModal(false);
    } catch (error) {
      console.log(error);
      showError("error");
    }
  }
  function onDeleteClicked() {
    setShowModal(!showModalDelete);
  }
  async function onDuplicate() {
    try {
      await OnboardingProtocol.duplicateProtocol(props.protocol._id);
      showSuccess("object_duplicated", { object: "protocolUpper" });
    } catch (error) {
      console.log(error);
      showError("error");
    }
  }

  const tasksLength = props?.protocol?.tasks.length || 0;
  return (
    <>
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button
              as="div"
              className={
                "arh-w-full arh-border arh-border-solid arh-border-t-0 arh-border-r-0 arh-border-b-borderTable arh-border-l-4 arh-py-4 arh-px-6 arh-cursor-pointer hover:arh-bg-greyColor" +
                (open
                  ? " arh-border-l-greenColor"
                  : " arh-border-l-transparent")
              }
              // onClick={toggleCollapse}
            >
              <div className="arh-w-full arh-flex arh-justify-between arh-items-center">
                <div className="arh-flex arh-items-center arh-space-x-8">
                  <div className={`${open && "arh-rotate-90"}`}>
                    {ShapeCompletDropdown()}
                  </div>
                  {inputWritable || props.isCreating ? (
                    <input
                      type="text"
                      id="input"
                      name=""
                      className="arh-w-96 arh-font-poppins arh-font-normal arh-text-xs arh-text-black arh-border arh-border-solid arh-border-greenColor arh-rounded-sm"
                      placeholder={t("Nommer le protocole")}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleUpsertProtocol}
                      ref={inputRef}
                      onKeyDown={handleKeyDown}
                    />
                  ) : (
                    <span className="arh-font-poppins arh-font-normal arh-text-xs arh-text-black">
                      {props.protocol.name}
                    </span>
                  )}
                </div>
                <div
                  className={
                    "arh-flex arh-items-center " + (open ? " arh-hidden" : "")
                  }
                >
                  <label className="arh-font-poppins arh-font-medium arh-text-[#393C40] arh-text-xs arh-mr-2 arh-mb-0">
                    {t("0 tâche", { count: tasksLength || 0 })}
                  </label>
                  <Menu
                    lists={[
                      {
                        icon: <SvgBlackTrash />,
                        title: "Effacer",
                        click: () => onDeleteClicked()
                      },
                      {
                        icon: <SvgChangeName />,
                        title: "Modifier",
                        click: () => onUpdateClicked()
                      },
                      {
                        icon: <SvgDuplicate />,
                        title: "Dupliquer",
                        click: () => onDuplicate()
                      }
                    ]}
                    withIcon
                  />
                </div>
              </div>
            </Disclosure.Button>

            <Disclosure.Panel>
              {props.protocol?._id && (
                <ProtocolTaskList protocol={props.protocol} />
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      <ModalDelete
        show={showModalDelete}
        onDelete={deleteProtocol}
        onCloseClicked={onDeleteClicked}
        object={t("ce protocole")}
      />
    </>
  );
}

export default ItemsIntegrationProtocol;
